import { useState } from 'react';

import { usePathname } from 'next/navigation';

import Close from '@icons/Close.svg';
import Logo from '@icons/Logo.svg';
import Menu from '@icons/Menu.svg';
import { useHeaderVisibilityStore } from '@providers/header-visibility-store-provider';
import { useNavigate } from 'hooks/useNavigation';
import { ROLES, RoleType } from 'types/roles';

import { StyledChevron } from '@components/Common';
import { MenuModal } from '@components/Modal';

import { extractRoleFromPath } from '@utils/helpers/extractRole';
import { signOut } from '@utils/supabase/auth-actions';

import { HeaderContainer, IconContainer, IconContainerButton, LogoContainer } from './styles';

export const Header = () => {
  const { goBack, goToPath } = useNavigate();
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const role = extractRoleFromPath(pathname || '') as RoleType;
  const showBackButton = useHeaderVisibilityStore((state) => state.showBackButton);
  const onBack = useHeaderVisibilityStore((state) => state.onBack);
  const isOnboarding = pathname?.endsWith('/onboarding');
  const isAdmin = pathname?.includes('/admin') || false;

  // Check if current page is the terms page (only public page with header)
  const isTermsPage = pathname?.includes('/terms');

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else {
      goBack();
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSignOut = async () => {
    await signOut(role);
  };

  const handleLogoClick = () => {
    // Don't navigate on public pages like terms
    if (isTermsPage) {
      return;
    }
    
    // Ensure role is valid before navigation
    if (!role || (role !== ROLES.TEACHER && role !== ROLES.SCHOOL && role !== ROLES.ADMIN)) {
      return;
    }
    
    // Redirect to home page based on role
    const homePath = role === ROLES.ADMIN ? `/${role}/dashboard` : `/${role}/shifts`;
    goToPath(homePath);
  };

  const disableButtonBack = !showBackButton;

  return (
    <HeaderContainer $isMenuOpen={isMenuOpen}>
      {disableButtonBack ? (
        <IconContainer />
      ) : (
        <IconContainerButton onClick={handleBackClick} aria-label="Go back">
          <StyledChevron />
        </IconContainerButton>
      )}
      <LogoContainer 
        onClick={handleLogoClick} 
        aria-label="Go to home page"
        $isClickable={!isTermsPage}
      >
        <Logo />
      </LogoContainer>
      {!isTermsPage ? (
        <>
          <IconContainerButton onClick={toggleMenu} aria-label="Open menu">
            {isMenuOpen ? <Close /> : <Menu />}
          </IconContainerButton>
          <MenuModal
            isOpen={isMenuOpen}
            onClose={toggleMenu}
            onClick={handleSignOut}
            role={role}
            onboardingFinished={!isOnboarding}
            isAdmin={isAdmin}
          />
        </>
      ) : (
        <IconContainer />
      )}
    </HeaderContainer>
  );
};
